import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import { IndustriesDropdownComponent } from '../../consultant/personal-data/dropdowns/industries-dropdown/industries-dropdown.component';
import { Requests } from '../../../shared/model/request.model';

/**
 * Review-field wrapper around the shared industries picker. The form control
 * holds the full `Requests[]`; the dropdown works with names, so we map between
 * them here.
 */
@Component({
  selector: 'app-cv-industries-field',
  standalone: true,
  imports: [IndustriesDropdownComponent],
  template: `
    <app-industries-dropdown
      [availableIndustries]="industries"
      [selectedIndustries]="selectedNames"
      [showSorting]="false"
      (industriesChange)="onChange($event)"
    ></app-industries-dropdown>
  `,
})
export class CvIndustriesFieldComponent implements OnInit {
  @Input() control!: FormControl;
  @Input() industries: Requests[] = [];

  selectedNames: string[] = [];

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.syncSelectedNames(this.currentBranches());
    // Keep the picker in sync when the control value is replaced programmatically.
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((branches) => this.syncSelectedNames((branches as Requests[]) ?? []));
  }

  onChange(names: string[]): void {
    const byName = new Map(this.industries.map((ind) => [ind.name, ind]));
    const selected = names.map(
      (name) => byName.get(name) ?? ({ id: name, name } as Requests),
    );
    this.control.setValue(selected);
  }

  private syncSelectedNames(branches: Requests[]): void {
    this.selectedNames = branches
      .map((branch) => branch.name)
      .filter((name): name is string => !!name);
  }

  private currentBranches(): Requests[] {
    return (this.control.value as Requests[]) ?? [];
  }
}
